import { createContext, useCallback, useContext, useMemo, useSyncExternalStore } from 'react';
import type { ReactElement, ReactNode } from 'react';

import type { ThemePreference, ThemeService } from '@/services/types';

/**
 * The React binding over `services/theme.ts` — docs/ARCHITECTURE.md §3, ADR-0016, ADR-0017.
 *
 * ═══ WHAT THIS FILE IS NOT ═══
 *
 * It is not a theme implementation. It reads no storage, touches no `matchMedia`, and writes
 * no attribute. `data-theme` on `<html>` is the ONE theme signal and the SERVICE owns it
 * (ADR-0017) — including before React exists, via the inline bootstrap in `index.html` whose
 * bytes `scripts/theme-bootstrap.mjs` keeps identical to the service. A second writer here
 * would be the legacy bug restated: `useTheme.ts` set both `classList.toggle('dark')` and
 * `setAttribute('data-theme')`, two signals that drifted the moment anything read only one.
 *
 * This file does exactly one thing the service cannot: it makes React re-render when the
 * preference changes.
 *
 * ═══ WHY useSyncExternalStore AND NOT useState + useEffect ═══
 *
 * The preference can change without any React event: the OS flips to dark at sunset while the
 * preference is `system` and the service's media listener fires. `useSyncExternalStore`
 * subscribes to that directly, gets tearing-freedom for free, and — because both snapshots
 * are STRINGS — never needs a memoised snapshot object to avoid an infinite re-render. That
 * is also why there are two stores rather than one returning `{ preference, resolved }`: the
 * object would be a new identity on every call and React would loop.
 *
 * `getServerSnapshot` is the same function as `getSnapshot`. The service is constructible with
 * no DOM at all (`createDetachedThemeService`), so there is nothing to guard against, and this
 * product is static-rendered rather than server-rendered anyway (ADR-0001).
 */

export interface ThemeContextValue {
  /** What the user chose: `'light' | 'dark' | 'system'`. */
  readonly preference: ThemePreference;
  /** What is on screen. Never `'system'` — CSS cannot resolve that word (ADR-0017). */
  readonly resolved: 'light' | 'dark';
  readonly setPreference: (preference: ThemePreference) => void;
  /** light → dark → system → light. See `THEME_CYCLE`. */
  readonly cycle: () => void;
}

/**
 * The order the toggle walks, and the reason it is a constant rather than a `switch`: the
 * toggle's `aria-label` has to name the NEXT state (docs/DESIGN.md §7.5), so the sequence is
 * read by two callers and must not exist twice.
 *
 * `system` is last rather than first because it is the default: a user who never touches the
 * control is already in it, and the first press should therefore produce a VISIBLE change.
 */
export const THEME_CYCLE = ['light', 'dark', 'system'] as const;

export function nextThemePreference(current: ThemePreference): ThemePreference {
  const index = THEME_CYCLE.indexOf(current);
  // `indexOf` returning -1 for an impossible value lands on `THEME_CYCLE[0]`, which is a
  // valid preference — a corrupted value must not be able to produce `undefined` here.
  return THEME_CYCLE[(index + 1) % THEME_CYCLE.length] ?? 'light';
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export interface ThemeProviderProps {
  /** The service instance from `createServices()`. */
  readonly theme: ThemeService;
  readonly children: ReactNode;
}

export function ThemeProvider({ theme, children }: ThemeProviderProps): ReactElement {
  const subscribe = useCallback((listener: () => void) => theme.subscribe(listener), [theme]);

  const preference = useSyncExternalStore(
    subscribe,
    () => theme.getPreference(),
    () => theme.getPreference(),
  );

  const resolved = useSyncExternalStore(
    subscribe,
    () => theme.getResolved(),
    () => theme.getResolved(),
  );

  const value = useMemo<ThemeContextValue>(
    () => ({
      preference,
      resolved,
      setPreference: (next: ThemePreference) => {
        theme.setPreference(next);
      },
      cycle: () => {
        theme.setPreference(nextThemePreference(theme.getPreference()));
      },
    }),
    [theme, preference, resolved],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/**
 * Throws, rather than returning a default, when the provider is missing. A silent fallback
 * would give a toggle that renders, looks right, and changes nothing — the exact failure that
 * is hardest to see in a screenshot and easiest to ship.
 */
export function useTheme(): ThemeContextValue {
  const value = useContext(ThemeContext);
  if (value === null) {
    throw new Error('useTheme() requires a <ThemeProvider theme={services.theme}> above it.');
  }
  return value;
}

export default ThemeProvider;
